/**
 * Purpose: Navigation between dashboard modules. ONE component, two variants —
 *          a vertical rail (desktop) and a scrollable tab strip (mobile) — so
 *          there is a single source of truth for nav behaviour and a11y.
 * Inputs: items ([{ id, label, icon, badge }]), activeId, onSelect(id),
 *         variant ('rail' | 'tabs'), className.
 * Outputs: tablist-semantics nav. Vertical-agnostic: knows nothing about
 *          businesses — any vertical passes its own descriptor list.
 */
import React from 'react';

const RAIL_ITEM =
  'flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-sm font-medium transition-colors';
const TAB_ITEM =
  'flex flex-shrink-0 items-center gap-2 rounded-full px-3.5 py-2 text-sm font-medium transition-colors';

const ACTIVE = {
  rail: 'bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200',
  tabs: 'bg-brand-600 text-white',
};

const INACTIVE = {
  rail: 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800',
  tabs:
    'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50 dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700 dark:hover:bg-gray-800',
};

const DashboardNav = ({ items = [], activeId, onSelect, variant = 'rail', className = '' }) => {
  if (!items.length) return null;

  const isRail = variant === 'rail';
  const containerClass = isRail
    ? 'flex flex-col gap-1'
    : 'scrollbar-hide flex gap-2 overflow-x-auto pb-1';

  return (
    <nav
      role="tablist"
      aria-orientation={isRail ? 'vertical' : 'horizontal'}
      aria-label="Dashboard sections"
      className={`${containerClass} ${className}`.trim()}
    >
      {items.map((item) => {
        const active = item.id === activeId;
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={active}
            aria-controls={`dashboard-panel-${item.id}`}
            onClick={() => onSelect?.(item.id)}
            className={[
              isRail ? RAIL_ITEM : TAB_ITEM,
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
              active ? ACTIVE[variant] : INACTIVE[variant],
            ].join(' ')}
          >
            {item.icon && <span className="[&>svg]:h-4 [&>svg]:w-4" aria-hidden>{item.icon}</span>}
            <span className="whitespace-nowrap">{item.label}</span>
            {item.badge != null && (
              <span
                className={[
                  'ml-auto rounded-full px-1.5 py-0.5 text-[11px] font-semibold',
                  active && !isRail
                    ? 'bg-white/20 text-white'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
                ].join(' ')}
              >
                {item.badge}
              </span>
            )}
          </button>
        );
      })}
    </nav>
  );
};

export default DashboardNav;
